import { ApiClient, coreApi } from '@givebacks/api';
import { NavLink as RouterLink } from 'react-router-dom';
import {
  ActionIcon,
  Affix,
  ButtonMantine,
  Card,
  Center,
  Checkbox,
  Collapse,
  Divider,
  Group,
  NavLink,
  ScrollArea,
  Stack,
  ThemeIcon,
  Title,
  Tooltip,
  rem,
} from 'ui';
import { useDisclosure } from 'ui/Hooks';
import { IconChevronDownTabler, IconGivebacks, IconInfo } from 'ui/Icons';

import type { InternalTool } from './types';
import { useInternalToolsMenu } from './useInternalToolsMenu';

const InternalToolsActions = () => {
  const [opened, handlers] = useDisclosure(false);
  return (
    <Stack>
      <Title order={3}>Actions</Title>

      <ButtonMantine
        variant="subtle"
        size="compact-sm"
        onClick={handlers.toggle}
        rightSection={
          <Group>
            <IconChevronDownTabler rotate={opened ? 180 : 0} brand="tabler" />{' '}
            <Center>
              <Tooltip label="Tools to debug session states">
                <IconInfo />
              </Tooltip>
            </Center>
          </Group>
        }
      >
        {opened ? 'Hide Actions' : 'Show Actions'}
      </ButtonMantine>

      <Collapse in={opened}>
        <Stack>
          <ButtonMantine
            variant="subtle"
            size="compact-sm"
            onClick={() => {
              void ApiClient.refreshToken();
            }}
            rightSection={
              <Group>
                <IconChevronDownTabler rotate={opened ? 180 : 0} brand="tabler" />
                <Center>
                  <Tooltip label="Manually refresh the session and get the new session">
                    <IconInfo />
                  </Tooltip>
                </Center>
              </Group>
            }
          >
            Refresh Session
          </ButtonMantine>

          <ButtonMantine
            variant="subtle"
            size="compact-sm"
            onClick={() => {
              void coreApi.delete('/users/sign_out');
            }}
            rightSection={
              <Group>
                <IconChevronDownTabler rotate={opened ? 180 : 0} brand="tabler" />{' '}
                <Center>
                  <Tooltip label="Manually delete the session without logging out">
                    <IconInfo />
                  </Tooltip>
                </Center>
              </Group>
            }
          >
            Delete Session
          </ButtonMantine>
        </Stack>
      </Collapse>
    </Stack>
  );
};

const InternalToolsLinks = () => {
  return (
    <Stack>
      <Title order={3}>STG Links</Title>

      <NavLink
        to="/student-fees/students?preview=true"
        component={RouterLink}
        label="Student Fees Preview"
      />
    </Stack>
  );
};

const Tool = ({ checked, label, tooltip, onChange }: InternalTool) => {
  return (
    <Group>
      <Checkbox
        label={label}
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
      />
      {tooltip && (
        <Tooltip label={tooltip}>
          <ThemeIcon variant="transparent" size="sm">
            <IconInfo />
          </ThemeIcon>
        </Tooltip>
      )}
    </Group>
  );
};

const InternalTools = () => {
  const { internalToolList } = useInternalToolsMenu();
  if (!internalToolList) return null;
  return internalToolList.map(({ key, ...rest }) => <Tool key={key} {...rest} />);
};

const Tools = () => {
  const { resetMenu } = useInternalToolsMenu();
  return (
    <Affix position={{ bottom: rem(80), right: rem(25) }} withinPortal={false}>
      <Card withBorder shadow="md" p="md">
        <Card.Section inheritPadding withBorder>
          <Group justify="space-between">
            <Title order={3}>Internal Tools</Title>
          </Group>
        </Card.Section>
        <Card.Section inheritPadding py="sm">
          <Stack>
            <ScrollArea.Autosize mah="500px">
              <Stack>
                <InternalTools />
                <Divider />
                <InternalToolsLinks />
                <Divider />
                <InternalToolsActions />
              </Stack>
            </ScrollArea.Autosize>
            <Center>
              <Tooltip label="Reset all tools to default values">
                <ButtonMantine variant="subtle" size="compact-sm" onClick={resetMenu}>
                  Reset
                </ButtonMantine>
              </Tooltip>
            </Center>
          </Stack>
        </Card.Section>
      </Card>
    </Affix>
  );
};

const ToolsWrapper = () => {
  return <Tools />;
};

const InternalToolsMenu = () => {
  const [opened, handlers] = useDisclosure(false);

  // Don't return on a production mode
  if (import.meta.env.VITE_API_ENV == 'production') return null;
  return (
    <>
      <Affix position={{ bottom: rem(20), right: rem(20) }} withinPortal zIndex={202}>
        <ActionIcon onClick={handlers.toggle} color={!opened ? 'cobalt' : 'tangerine'}>
          <IconGivebacks />
        </ActionIcon>
      </Affix>
      {opened && <ToolsWrapper />}
    </>
  );
};
InternalToolsMenu.displayName = 'InternalToolsMenu';

export { InternalToolsMenu };
